import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, Eye, MousePointerClick, CreditCard,
  ShoppingBag, DollarSign, Package, Crown, BarChart3, ArrowDown,
  Tag, AlertTriangle, Mail, ExternalLink, ArrowRight, Loader2,
  RefreshCw, Calendar, Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_OPTIONS = [
  { label: 'I dag', value: 1 },
  { label: '7 dager', value: 7 },
  { label: '30 dager', value: 30 },
  { label: '90 dager', value: 90 },
  { label: '365 dager', value: 365 },
];

const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-500' },
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-500' },
  violet:  { bg: 'bg-violet-50',  text: 'text-violet-500' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-500' },
  rose:    { bg: 'bg-rose-50',    text: 'text-rose-500' },
  stone:   { bg: 'bg-stone-50',   text: 'text-stone-500' },
};

const MetricCard = ({ icon: Icon, label, value, subtext, color = 'stone', testId }) => (
  <div data-testid={testId} className="bg-white rounded-xl border border-stone-100 p-5">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-9 h-9 rounded-lg ${COLOR_MAP[color]?.bg || 'bg-stone-50'} flex items-center justify-center`}>
        <Icon className={`w-4.5 h-4.5 ${COLOR_MAP[color]?.text || 'text-stone-500'}`} strokeWidth={1.5} />
      </div>
    </div>
    <p className="text-2xl font-serif font-bold text-stone-900 tracking-tight">{value}</p>
    <p className="text-xs font-sans text-stone-500 mt-0.5">{label}</p>
    {subtext && <p className="text-[10px] font-sans text-stone-400 mt-1">{subtext}</p>}
  </div>
);

const FunnelStep = ({ step, index, total }) => {
  const isLast = index === total - 1;
  const stepLabels = {
    product_view: { label: 'Views', icon: Eye },
    preview_started: { label: 'Preview startet', icon: MousePointerClick },
    checkout_started: { label: 'Checkout startet', icon: CreditCard },
    checkout_completed: { label: 'Purchase completed', icon: ShoppingBag },
  };
  const info = stepLabels[step.step] || { label: step.step, icon: BarChart3 };
  const Icon = info.icon;
  const barWidth = Math.max(step.count > 0 ? 15 : 5, step.conversion_rate);

  return (
    <div>
      <div className="flex items-center gap-4">
        <div className="w-9 h-9 rounded-lg bg-stone-900 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-sans font-semibold text-stone-800">{info.label}</span>
            <span className="text-sm font-serif font-bold text-stone-900">{step.count.toLocaleString()}</span>
          </div>
          <div className="h-2.5 bg-stone-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-stone-900 rounded-full transition-all duration-700"
              style={{ width: `${barWidth}%` }}
            />
          </div>
        </div>
      </div>
      {!isLast && step.dropoff_percent > 0 && (
        <div className="flex items-center gap-2 ml-[52px] my-1.5">
          <ArrowDown className="w-3 h-3 text-red-400" />
          <span className="text-[10px] font-sans text-red-500 font-medium">
            {step.dropoff_percent}% frafall
          </span>
          <span className="text-[10px] font-sans text-stone-400 ml-auto">
            {step.conversion_rate}% konvertering
          </span>
        </div>
      )}
    </div>
  );
};

const RankingTable = ({ title, items, columns, testId, onProductClick }) => {
  if (!items || items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-stone-100 p-5">
        <h4 className="text-sm font-sans font-bold text-stone-800 mb-3">{title}</h4>
        <p className="text-xs font-sans text-stone-400 italic">None data yet</p>
      </div>
    );
  }
  return (
    <div data-testid={testId} className="bg-white rounded-xl border border-stone-100 p-5 overflow-hidden">
      <h4 className="text-sm font-sans font-bold text-stone-800 mb-3">{title}</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-stone-100">
              <th className="text-[10px] font-sans font-bold text-stone-400 uppercase tracking-wider py-2 pr-3">#</th>
              {columns.map((col, i) => (
                <th key={i} className="text-[10px] font-sans font-bold text-stone-400 uppercase tracking-wider py-2 pr-3">{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.slice(0, 8).map((item, idx) => (
              <tr key={idx} className="border-b border-stone-50 last:border-0 group">
                <td className="text-xs font-sans text-stone-400 py-2 pr-3">{idx + 1}</td>
                {columns.map((col, i) => (
                  <td key={i} className={`text-xs font-sans py-2 pr-3 ${i === 0 ? 'text-stone-800 font-medium' : 'text-stone-600'}`}>
                    {i === 0 && col.key === 'slug' && onProductClick ? (
                      <button
                        data-testid={`drilldown-link-${item.slug}`}
                        onClick={() => onProductClick(item.slug)}
                        className="text-stone-800 hover:text-stone-950 underline decoration-stone-200 hover:decoration-stone-400 transition-colors cursor-pointer"
                      >
                        {col.format ? col.format(item[col.key], item) : item[col.key]}
                      </button>
                    ) : (
                      col.format ? col.format(item[col.key], item) : item[col.key]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const ConversionFunnelDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);
  const [targetType, setTargetType] = useState('');

  const handleProductClick = (slug) => navigate(`/admin/products/${slug}`);

  const fetchDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ days });
      if (targetType) params.set('target_type', targetType);
      const res = await axios.get(`${API}/analytics/dashboard?${params}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not laste dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, [days, targetType]);

  const fmtCurrency = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtSlug = (slug) => (slug || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
        <p className="text-sm font-sans text-stone-600">{error}</p>
        <Button variant="outline" className="mt-4 rounded-full" onClick={fetchDashboard}>Try again</Button>
      </div>
    );
  }

  if (!data) return null;

  const { funnel, revenue, abandoned, rankings, recovery } = data;

  return (
    <div data-testid="conversion-funnel-dashboard" className="space-y-8">
      {/* Period filter + refresh */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar className="w-4 h-4 text-stone-400" />
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              data-testid={`period-${opt.value}`}
              onClick={() => setDays(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-sans font-bold transition-all ${
                days === opt.value ? 'bg-stone-900 text-white' : 'bg-stone-50 text-stone-500 hover:bg-stone-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-stone-400" />
          <select
            data-testid="target-type-filter"
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
            className="text-xs font-sans bg-white border border-stone-200 rounded-lg px-2.5 py-1.5 text-stone-700"
          >
            <option value="">All typer</option>
            <option value="product">Enkeltproducts</option>
            <option value="bundle">Collectioner</option>
            <option value="subscription">Pro-abonnement</option>
          </select>
          <button
            data-testid="refresh-dashboard-btn"
            onClick={fetchDashboard}
            className="p-1.5 rounded-lg bg-stone-50 hover:bg-stone-100 text-stone-500 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Revenue overview */}
      <div>
        <h3 className="text-sm font-sans font-bold text-stone-800 uppercase tracking-wider mb-4">Inntektsoversikt</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <MetricCard testId="metric-total-revenue" icon={DollarSign} label="Total inntekt" value={fmtCurrency(revenue.total)} color="emerald" />
          <MetricCard testId="metric-product-revenue" icon={ShoppingBag} label="Products" value={fmtCurrency(revenue.product.amount)} subtext={`${revenue.product.count} salg`} color="blue" />
          <MetricCard testId="metric-bundle-revenue" icon={Package} label="Collectioner" value={fmtCurrency(revenue.bundle.amount)} subtext={`${revenue.bundle.count} salg`} color="violet" />
          <MetricCard testId="metric-pro-revenue" icon={Crown} label="Pro-abonnement" value={fmtCurrency(revenue.pro_subscription.amount)} subtext={`${revenue.pro_subscription.count} subs`} color="amber" />
          <MetricCard testId="metric-coupon-revenue" icon={Tag} label="Kupong-tilskrevet" value={fmtCurrency(revenue.coupon_attributed.amount)} subtext={`${revenue.coupon_attributed.count} brukt, -${fmtCurrency(revenue.coupon_attributed.discount_given)}`} color="rose" />
          <MetricCard testId="metric-aov" icon={TrendingUp} label="Snitt ordreverd" value={fmtCurrency(revenue.average_order_value)} color="stone" />
        </div>
      </div>

      {/* Conversion funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-stone-100 p-6">
          <h3 className="text-sm font-sans font-bold text-stone-800 uppercase tracking-wider mb-1">Konverteringstrakt</h3>
          <p className="text-[11px] font-sans text-stone-400 mb-5">Siste {days} dager</p>
          <div className="space-y-2">
            {funnel.map((step, i) => (
              <FunnelStep key={step.step} step={step} index={i} total={funnel.length} />
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-stone-100 grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-sans text-stone-400 uppercase tracking-wider">Overordnet konvertering</p>
              <p className="text-xl font-serif font-bold text-stone-900">{data.overall_conversion_rate}%</p>
              <p className="text-[10px] font-sans text-stone-400">View &rarr; Purchase</p>
            </div>
            <div>
              <p className="text-[10px] font-sans text-stone-400 uppercase tracking-wider">Checkout-konvertering</p>
              <p className="text-xl font-serif font-bold text-stone-900">{data.checkout_conversion_rate}%</p>
              <p className="text-[10px] font-sans text-stone-400">Checkout &rarr; Purchase</p>
            </div>
          </div>
        </div>

        {/* Abandoned + Recovery */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h3 className="text-sm font-sans font-bold text-stone-800 uppercase tracking-wider mb-4">Forlatte handlevogner</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-2xl font-serif font-bold text-red-600">{abandoned.abandon_rate}%</p>
                <p className="text-xs font-sans text-stone-500">Frafallsrate</p>
              </div>
              <div>
                <p className="text-2xl font-serif font-bold text-stone-900">{fmtCurrency(abandoned.abandoned_value)}</p>
                <p className="text-xs font-sans text-stone-500">Tapt verdi</p>
              </div>
              <div>
                <p className="text-lg font-serif font-bold text-stone-700">{abandoned.started}</p>
                <p className="text-[10px] font-sans text-stone-400">Checkout startet</p>
              </div>
              <div>
                <p className="text-lg font-serif font-bold text-emerald-600">{abandoned.completed}</p>
                <p className="text-[10px] font-sans text-stone-400">Fullfort</p>
              </div>
            </div>
            {abandoned.most_abandoned?.length > 0 && (
              <>
                <p className="text-[10px] font-sans font-bold text-stone-400 uppercase tracking-wider mb-2">Mest forlatt</p>
                <div className="space-y-1.5">
                  {abandoned.most_abandoned.slice(0, 5).map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs font-sans">
                      <button onClick={() => handleProductClick(item.slug)} className="text-stone-700 hover:text-stone-900 underline decoration-stone-200 hover:decoration-stone-400 transition-colors">{fmtSlug(item.slug)}</button>
                      <span className="text-stone-500">{item.count}x ({fmtCurrency(item.value)})</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h3 className="text-sm font-sans font-bold text-stone-800 uppercase tracking-wider mb-4">
              <Mail className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
              Gjenopprettingssystem
            </h3>

            {/* Overview metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <div data-testid="recovery-emails-sent" className="bg-stone-50 rounded-lg p-3 text-center">
                <p className="text-lg font-serif font-bold text-stone-900">{recovery.emails_sent}</p>
                <p className="text-[10px] font-sans text-stone-400">Emailer sendt</p>
              </div>
              <div data-testid="recovery-ctr" className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-lg font-serif font-bold text-blue-600">{recovery.click_through_rate}%</p>
                <p className="text-[10px] font-sans text-stone-400">Klikkrate</p>
              </div>
              <div data-testid="recovery-purchases" className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-lg font-serif font-bold text-emerald-600">{recovery.total_recovered || recovery.purchases_recovered}</p>
                <p className="text-[10px] font-sans text-stone-400">Gjenvunnet</p>
              </div>
              <div data-testid="recovery-revenue" className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-lg font-serif font-bold text-amber-600">{fmtCurrency(recovery.recovered_revenue)}</p>
                <p className="text-[10px] font-sans text-stone-400">Gjenvunnet inntekt</p>
              </div>
            </div>

            {/* Email 1 vs Email 2 comparison */}
            <div className="mb-5">
              <p className="text-[10px] font-sans font-bold text-stone-400 uppercase tracking-wider mb-2">Email 1 vs Email 2</p>
              <div className="grid grid-cols-2 gap-3">
                <div data-testid="recovery-email-1" className="bg-stone-50 rounded-lg p-3">
                  <p className="text-xs font-sans font-semibold text-stone-700 mb-1">Email 1 — Paaminnelse</p>
                  <p className="text-sm font-serif font-bold text-stone-900">{recovery.email_1_sent || 0} sendt</p>
                  <p className="text-[10px] text-stone-400">None kupong</p>
                </div>
                <div data-testid="recovery-email-2" className="bg-amber-50 rounded-lg p-3">
                  <p className="text-xs font-sans font-semibold text-amber-700 mb-1">Email 2 — Kupong</p>
                  <p className="text-sm font-serif font-bold text-amber-900">{recovery.email_2_sent || 0} sendt</p>
                  <p className="text-[10px] text-amber-600">15% recovery-kupong</p>
                </div>
              </div>
            </div>

            {/* Recovery coupon metrics */}
            {recovery.coupon && (
              <div className="mb-3">
                <p className="text-[10px] font-sans font-bold text-stone-400 uppercase tracking-wider mb-2">Recovery-kupong ytelse</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div data-testid="coupon-generated" className="text-center">
                    <p className="text-sm font-serif font-bold text-stone-800">{recovery.coupon.total_generated}</p>
                    <p className="text-[10px] font-sans text-stone-400">Generert</p>
                  </div>
                  <div data-testid="coupon-redeemed" className="text-center">
                    <p className="text-sm font-serif font-bold text-emerald-600">{recovery.coupon.total_redeemed}</p>
                    <p className="text-[10px] font-sans text-stone-400">Innlost</p>
                  </div>
                  <div data-testid="coupon-redemption-rate" className="text-center">
                    <p className="text-sm font-serif font-bold text-blue-600">{recovery.coupon.redemption_rate}%</p>
                    <p className="text-[10px] font-sans text-stone-400">Innl.rate</p>
                  </div>
                  <div data-testid="coupon-revenue" className="text-center">
                    <p className="text-sm font-serif font-bold text-amber-600">{fmtCurrency(recovery.coupon.revenue_with_coupon)}</p>
                    <p className="text-[10px] font-sans text-stone-400">Kuponginntekt</p>
                  </div>
                </div>
                {(recovery.recovered_with_coupon > 0 || recovery.recovered_without_coupon > 0) && (
                  <div className="mt-3 flex items-center gap-2 text-[10px] font-sans text-stone-500">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-400" /> Med kupong: {recovery.recovered_with_coupon}
                    <span className="inline-block w-2 h-2 rounded-full bg-stone-300 ml-2" /> Uten kupong: {recovery.recovered_without_coupon}
                  </div>
                )}
              </div>
            )}

            {/* Recent recovery events */}
            {recovery.recent_events?.length > 0 && (
              <details className="mt-3">
                <summary className="text-[10px] font-sans font-bold text-stone-400 uppercase tracking-wider cursor-pointer hover:text-stone-600">
                  Siste recovery-hendelser ({recovery.recent_events.length})
                </summary>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {recovery.recent_events.slice(0, 10).map((evt, i) => (
                    <div key={evt.id || i} className="flex items-center justify-between text-[11px] font-sans py-1 border-b border-stone-50">
                      <span className="text-stone-600 truncate max-w-[120px]">{evt.user_email}</span>
                      <span className="text-stone-500 truncate max-w-[100px]">{fmtSlug(evt.product_slug)}</span>
                      <span className={`font-medium ${evt.recovered ? 'text-emerald-600' : 'text-amber-500'}`}>
                        {evt.recovered ? (evt.recovered_with_coupon ? 'Gjenvunnet (kupong)' : 'Gjenvunnet') : 'Venter'}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>

      {/* Rankings */}
      <div>
        <h3 className="text-sm font-sans font-bold text-stone-800 uppercase tracking-wider mb-4">Productrangeringer</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <RankingTable
            testId="ranking-most-viewed"
            title="Mest sett"
            items={rankings.most_viewed}
            onProductClick={handleProductClick}
            columns={[
              { key: 'slug', label: 'Product', format: fmtSlug },
              { key: 'views', label: 'Views' },
              { key: 'type', label: 'Type', format: (v) => v === 'bundle' ? 'Collection' : 'Product' },
            ]}
          />
          <RankingTable
            testId="ranking-most-purchased"
            title="Most purchased"
            items={rankings.most_purchased}
            onProductClick={handleProductClick}
            columns={[
              { key: 'slug', label: 'Product', format: fmtSlug },
              { key: 'purchases', label: 'Purchase' },
              { key: 'revenue', label: 'Inntekt', format: fmtCurrency },
            ]}
          />
          <RankingTable
            testId="ranking-highest-revenue"
            title="Hoyest inntekt"
            items={rankings.highest_revenue}
            onProductClick={handleProductClick}
            columns={[
              { key: 'slug', label: 'Product', format: fmtSlug },
              { key: 'revenue', label: 'Inntekt', format: fmtCurrency },
              { key: 'count', label: 'Antall' },
            ]}
          />
          <RankingTable
            testId="ranking-highest-conversion"
            title="Hoyest konvertering"
            items={rankings.highest_conversion}
            onProductClick={handleProductClick}
            columns={[
              { key: 'slug', label: 'Product', format: fmtSlug },
              { key: 'rate', label: 'Rate', format: (v) => `${v}%` },
              { key: 'views', label: 'Shown.' },
            ]}
          />
          <RankingTable
            testId="ranking-lowest-conversion"
            title="Lavest konvertering"
            items={rankings.lowest_conversion}
            onProductClick={handleProductClick}
            columns={[
              { key: 'slug', label: 'Product', format: fmtSlug },
              { key: 'rate', label: 'Rate', format: (v) => `${v}%` },
              { key: 'views', label: 'Shown.' },
            ]}
          />
          <RankingTable
            testId="ranking-most-previewed"
            title="Most previewed"
            items={rankings.most_previewed}
            onProductClick={handleProductClick}
            columns={[
              { key: 'slug', label: 'Product', format: fmtSlug },
              { key: 'previews', label: 'Previews' },
            ]}
          />
        </div>
      </div>

      {/* ─── Smart Recommendations ─── */}
      {data?.recommendations && (
        <div className="mt-10">
          <h3 data-testid="rec-analytics-heading" className="text-lg font-serif font-bold text-stone-800 mb-4">Smart Recommendations</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <MetricCard testId="rec-impressions" icon={Eye} label="Impressions" value={data.recommendations.impressions} color="blue" />
            <MetricCard testId="rec-clicks" icon={MousePointerClick} label="Clicks" value={data.recommendations.clicks} color="violet" />
            <MetricCard testId="rec-ctr" icon={TrendingUp} label="CTR" value={`${data.recommendations.ctr}%`} color="emerald" />
            <MetricCard testId="rec-revenue" icon={DollarSign} label="Revenue" value={fmtCurrency(data.recommendations.revenue)} color="amber" />
            <MetricCard testId="rec-checkouts" icon={CreditCard} label="Checkouts" value={data.recommendations.checkouts_started} color="stone" />
            <MetricCard testId="rec-purchases" icon={ShoppingBag} label="Purchases" value={data.recommendations.purchases} color="emerald" />
            <MetricCard testId="rec-checkout-rate" icon={ArrowRight} label="Click→Checkout" value={`${data.recommendations.checkout_rate}%`} color="blue" />
            <MetricCard testId="rec-purchase-rate" icon={ArrowRight} label="Checkout→Purchase" value={`${data.recommendations.purchase_rate}%`} color="violet" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {data.recommendations.top_clicked?.length > 0 && (
              <RankingTable
                testId="rec-top-clicked"
                title="Top Recommended (clicks)"
                items={data.recommendations.top_clicked}
                onProductClick={handleProductClick}
                columns={[
                  { key: 'slug', label: 'Product', format: fmtSlug },
                  { key: 'clicks', label: 'Clicks' },
                ]}
              />
            )}
            {data.recommendations.top_placements?.length > 0 && (
              <RankingTable
                testId="rec-top-placements"
                title="Top Placements (clicks)"
                items={data.recommendations.top_placements}
                columns={[
                  { key: 'placement', label: 'Placement' },
                  { key: 'clicks', label: 'Clicks' },
                ]}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
